import { Schemas } from '@api-client/generated/types';

type Company = Schemas.AdminCompanyResponseDto;

export interface MissingInvoicingField {
  key: string;
  label: string;
}

/**
 * Company fields the backend requires before a billing invoice can be issued
 * (mirrors `getMissingInvoiceFields` in the backend billing service). While any
 * of these are missing, billing cannot be enabled and no period/one-time
 * invoice can be created.
 *
 * Note: vatId / iban / registrationNumber (RCS) are intentionally NOT required
 * — the backend does not block invoicing on them.
 *
 * @param company - The company DTO (may be undefined while loading)
 * @returns The list of missing required fields (empty when ready to invoice)
 */
export const getMissingInvoicingFields = (
  company?: Company | null
): MissingInvoicingField[] => {
  const details = company?.details;
  const address = details?.address;
  const contact = details?.contactPerson;

  const requirements: Array<[isPresent: boolean, key: string, label: string]> =
    [
      [Boolean(company?.name), 'name', 'Company name'],
      [Boolean(company?.referenceId), 'referenceId', 'Client reference'],
      [Boolean(address?.houseNumber), 'houseNumber', 'House number'],
      [Boolean(address?.street), 'street', 'Street'],
      [Boolean(address?.postalCode), 'postalCode', 'Postal code'],
      [Boolean(address?.city), 'city', 'City'],
      [Boolean(address?.countryCode), 'countryCode', 'Country'],
      [Boolean(contact?.firstName), 'contactFirstName', 'Contact first name'],
      [Boolean(contact?.lastName), 'contactLastName', 'Contact last name'],
      [Boolean(contact?.email), 'contactEmail', 'Contact email'],
    ];

  return requirements.flatMap(([isPresent, key, label]) =>
    isPresent ? [] : [{ key, label }]
  );
};

/**
 * Whether the company has all the data required to issue billing invoices.
 */
export const canIssueInvoices = (company?: Company | null): boolean =>
  getMissingInvoicingFields(company).length === 0;
